import Phaser from 'phaser';
import { WORLD_INTERACTION_INPUT } from '@/config/input.config';
import { Haptics } from '@/game/fx/Haptics';

export type WorldInteractionProfile = 'standard' | 'quickHold';
export type WorldInteractionCompletionFeedback = 'success' | 'neutral';

export interface WorldInteractionOptions {
  /** Szybki profil dla poruszających się awatarów innych graczy. */
  profile?: WorldInteractionProfile;
  /** Explicit hold on both mouse and touch, for intentional portal entry. */
  holdMs?: number;
  onTap?: () => void;
  /**
   * Dla ruchomego celu false: awatar może sam wybiec spod nieruchomego palca.
   * Dla stałych obiektów wyjście palca poza hitbox anuluje hold.
   */
  cancelOnPointerOut?: boolean;
  /**
   * Dobiera feedback ukończenia. `neutral` zachowuje callback (np. komunikat
   * cooldownu), ale nie pokazuje zieleni ani nie uruchamia haptyki sukcesu.
   */
  completionFeedback?: () => WorldInteractionCompletionFeedback;
}

interface ActiveHold {
  readonly pointer: Phaser.Input.Pointer;
  readonly pointerId: number;
  readonly target: Phaser.GameObjects.GameObject;
  readonly action: () => void;
  readonly onTap?: () => void;
  readonly completionFeedback?: () => WorldInteractionCompletionFeedback;
  readonly cancelDistancePx: number;
  tween: Phaser.Tweens.Tween | null;
}

/**
 * Jeden arbiter dotykowych interakcji dla sceny.
 *
 * Mysz zachowuje szybki click. Dotyk musi pozostać nieruchomy do wypełnienia
 * pierścienia. Przesunięcie palca oznacza zamiar sterowania joystickiem i
 * natychmiast anuluje akcję. Kontroler utrzymuje najwyżej jeden aktywny hold,
 * dzięki czemu nakładające się hitboxy nie uruchomią dwóch callbacków.
 */
export class WorldInteractionController {
  private readonly indicator: Phaser.GameObjects.Container;
  private readonly track: Phaser.GameObjects.Arc;
  private readonly progressRing: Phaser.GameObjects.Arc;
  private readonly center: Phaser.GameObjects.Arc | Phaser.GameObjects.Image;
  private activeHold: ActiveHold | null = null;
  private feedbackTween: Phaser.Tweens.Tween | null = null;
  private destroyed = false;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly canInteract: () => boolean,
  ) {
    this.track = scene.add
      .circle(0, 0, 29, 0x102f3a, 0.72)
      .setStrokeStyle(5, 0xffffff, 0.72);
    this.progressRing = scene.add
      .arc(0, 0, 33, -90, -89.9, false, 0xffffff, 0)
      .setStrokeStyle(6, 0x20c9c6, 1)
      .setClosePath(false);
    this.center = scene.textures.exists('ui_interact_tap')
      ? scene.add.image(0, 0, 'ui_interact_tap').setDisplaySize(42, 42)
      : scene.add.circle(0, 0, 8, 0xffffff, 0.92);

    this.indicator = scene.add
      .container(0, 0, [this.track, this.center, this.progressRing])
      .setScrollFactor(0)
      .setDepth(1_000_000)
      .setVisible(false);

    scene.input.on(Phaser.Input.Events.POINTER_MOVE, this.handlePointerMove, this);
    scene.input.on(Phaser.Input.Events.POINTER_UP, this.handlePointerUp, this);
    scene.input.on(Phaser.Input.Events.GAME_OUT, this.handleGameOut, this);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, this.destroy, this);
  }

  /** Podpina spójny click/hold do wcześniej ustawionego interactive GameObjectu. */
  bind(
    target: Phaser.GameObjects.GameObject,
    action: () => void,
    options: WorldInteractionOptions = {},
  ): void {
    target.on('pointerdown', (
      pointer: Phaser.Input.Pointer,
      _localX: number,
      _localY: number,
      event: Phaser.Types.Input.EventData,
    ) => {
      event.stopPropagation();
      if (!this.canInteract()) return;

      // Desktop pozostaje szybki. `wasTouch` pochodzi z aktualnego DOM eventu,
      // więc laptop 2-w-1 nadal rozróżnia mysz od rzeczywistego dotyku.
      if (!pointer.wasTouch && options.holdMs === undefined) {
        this.cancelHold(false);
        action();
        return;
      }

      this.startHold(pointer, target, action, options);
    });

    if (options.cancelOnPointerOut !== false) {
      target.on('pointerout', (pointer: Phaser.Input.Pointer) => {
        if (this.activeHold?.pointerId === pointer.id) this.cancelHold();
      });
    }

    target.once(Phaser.GameObjects.Events.DESTROY, () => {
      if (this.activeHold?.target === target) this.cancelHold(false);
    });
  }

  private startHold(
    pointer: Phaser.Input.Pointer,
    target: Phaser.GameObjects.GameObject,
    action: () => void,
    options: WorldInteractionOptions,
  ): void {
    this.cancelHold(false);
    this.feedbackTween?.remove();
    this.feedbackTween = null;
    this.scene.tweens.killTweensOf(this.indicator);

    const duration = options.holdMs ?? (options.profile === 'quickHold'
      ? WORLD_INTERACTION_INPUT.movingPlayerHoldMs
      : WORLD_INTERACTION_INPUT.standardHoldMs);
    const progressState = { value: 0 };

    this.indicator
      .setPosition(
        pointer.x,
        Math.max(38, pointer.y - WORLD_INTERACTION_INPUT.indicatorOffsetY),
      )
      .setScale(0.84)
      .setAlpha(0)
      .setVisible(true);
    this.track.setStrokeStyle(5, 0xffffff, 0.72);
    this.progressRing
      .setStrokeStyle(6, 0x20c9c6, 1)
      .setStartAngle(-90)
      .setEndAngle(-89.9);

    this.scene.tweens.add({
      targets: this.indicator,
      scaleX: 1,
      scaleY: 1,
      alpha: 1,
      duration: 80,
      ease: 'Back.easeOut',
    });

    const active: ActiveHold = {
      pointer,
      pointerId: pointer.id,
      target,
      action,
      onTap: options.onTap,
      ...(options.completionFeedback && {
        completionFeedback: options.completionFeedback,
      }),
      cancelDistancePx: WORLD_INTERACTION_INPUT.cancelDistancePx,
      tween: null,
    };
    active.tween = this.scene.tweens.add({
      targets: progressState,
      value: 1,
      duration,
      ease: 'Linear',
      onUpdate: () => {
        if (this.activeHold !== active) return;
        this.progressRing.setEndAngle(-90 + 359.9 * progressState.value);
      },
      onComplete: () => this.completeHold(active),
    });
    this.activeHold = active;
  }

  private completeHold(active: ActiveHold): void {
    if (
      this.destroyed ||
      this.activeHold !== active ||
      !active.pointer.isDown ||
      active.pointer.wasCanceled ||
      active.pointer.getDistance() > active.cancelDistancePx ||
      !this.canInteract()
    ) {
      this.cancelHold();
      return;
    }

    this.activeHold = null;
    const isSuccess = active.completionFeedback?.() !== 'neutral';
    this.progressRing
      .setEndAngle(269.9)
      .setStrokeStyle(isSuccess ? 7 : 6, isSuccess ? 0x6ee7a8 : 0xcbd5e1, 1);
    this.track.setStrokeStyle(5, isSuccess ? 0xd8ffe9 : 0xe2e8f0, 1);
    if (isSuccess) Haptics.worldInteractionConfirmed();

    this.feedbackTween = this.scene.tweens.add({
      targets: this.indicator,
      scaleX: 1.16,
      scaleY: 1.16,
      alpha: 0,
      duration: 150,
      ease: 'Quad.easeOut',
      onComplete: () => {
        this.indicator.setVisible(false);
        this.feedbackTween = null;
      },
    });

    active.action();
  }

  private handlePointerMove(pointer: Phaser.Input.Pointer): void {
    const active = this.activeHold;
    if (!active || pointer.id !== active.pointerId) return;
    if (pointer.getDistance() > active.cancelDistancePx) this.cancelHold();
  }

  private handlePointerUp(pointer: Phaser.Input.Pointer): void {
    const active = this.activeHold;
    if (active?.pointerId !== pointer.id) return;
    const tap = !pointer.wasCanceled && pointer.getDistance() <= active.cancelDistancePx && this.canInteract();
    this.cancelHold();
    if (tap) active.onTap?.();
  }

  private handleGameOut(): void {
    this.cancelHold();
  }

  private cancelHold(animate = true): void {
    const active = this.activeHold;
    if (!active) return;
    this.activeHold = null;
    active.tween?.remove();

    if (!animate || this.destroyed) {
      this.scene.tweens.killTweensOf(this.indicator);
      this.indicator.setVisible(false);
      return;
    }
    this.feedbackTween?.remove();
    this.scene.tweens.killTweensOf(this.indicator);
    this.feedbackTween = this.scene.tweens.add({
      targets: this.indicator,
      scaleX: 0.9,
      scaleY: 0.9,
      alpha: 0,
      duration: 90,
      ease: 'Quad.easeIn',
      onComplete: () => {
        this.indicator.setVisible(false);
        this.feedbackTween = null;
      },
    });
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.cancelHold(false);
    this.feedbackTween?.remove();
    this.feedbackTween = null;
    this.scene.tweens.killTweensOf(this.indicator);
    this.scene.input.off(Phaser.Input.Events.POINTER_MOVE, this.handlePointerMove, this);
    this.scene.input.off(Phaser.Input.Events.POINTER_UP, this.handlePointerUp, this);
    this.scene.input.off(Phaser.Input.Events.GAME_OUT, this.handleGameOut, this);
    this.indicator.destroy(true);
  }
}
